/* eslint-disable import/extensions */
/* eslint-disable object-curly-newline */
/* eslint-disable camelcase */
import React, { useMemo, useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Link, useLocation } from 'react-router-dom'
import { getById, getImgByTitle } from '@apps/utils/contentful'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { setPageAnalytics } from '@apps/utils/Analytics'
import { ANALYTICS_EVENT_TYPES } from '@apps/utils/constants'
import { Button as KoodoButton, Image, Typography } from '@telus/koodo-ui-kit'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'
import Box from '@/apps/components/Box'
import useIsMobileScreen from '@/apps/hooks/isMobileScreen'

const FOOTER_LINK_TOKENS = {
  color: 'var(--color-primary)',
  fontName: 'StagSans',
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: '24px'
}

const SimSelectionFooter = ({
  previousStepLabel = '',
  previousStepLink = '',
  nextStepLabel,
  onNextStepClick: onNextStepClickProp,
  nextStepTextButton,
  hideNextStep = false,
  dataTestid = '',
  nextStepLoading = false,
  compactSpacing = false
}) => {
  const location = useLocation()
  const isMobile = useIsMobileScreen()

  const {
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language,
    [APPLICATION_STATE.CURRENT_REGION]: region
  } = useContext(ApplicationContext)

  const {
    content: { pageContent },
    user: { selectedLanguage }
  } = useSelector((state) => state.common)
  const { simType } = useSelector((state) => state.simSelectionPage.selectSimType || {})

  const hasPreviousStep = Boolean(previousStepLabel)
  const [internalNextStepLoading, setInternalNextStepLoading] = useState(false)
  const isNextStepLoading = nextStepLoading || internalNextStepLoading
  const footerPaddingClass = compactSpacing ? 'py-0' : 'py-4'
  const buttonMarginClass = compactSpacing ? '' : 'my-4'
  const previousStepMarginClass = compactSpacing ? 'my-0' : 'my-4'

  const tagManagerInfo = useTagManagerInfo()

  const pushClickAnalytics = ({
    click_text,
    journey_type,
    event_type,
    field_warning,
    error_response_code,
    event_click = ANALYTICS_EVENT_TYPES.CLICK
  } = {}) => {
    const analyticsInfo = {
      ...tagManagerInfo,
      click_text,
      journey_type,
      field_warning,
      error_response_code
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      setPageAnalytics(location, 'click', region, language, analyticsInfo, event_type, event_click)
    )
  }

  const handlePreviousStepClick = () => {
    pushClickAnalytics({ click_text: previousStepLabel, journey_type: simType, event_type: previousStepLabel })
  }

  const onNextStepClick = async (event) => {
    if (isNextStepLoading) return

    if (nextStepLabel?.includes('eSIM')) {
      pushClickAnalytics({ click_text: nextStepLabel, journey_type: 'eSIM', event_type: 'Proceed with eSIM link clicked' })
    } else {
      pushClickAnalytics({ click_text: nextStepLabel, journey_type: simType, event_type: nextStepLabel })
    }
    if (onNextStepClickProp) {
      setInternalNextStepLoading(true)
      try {
        await onNextStepClickProp(event)
      } catch (error) {
        pushClickAnalytics({
          click_text: nextStepLabel,
          journey_type: simType,
          event_type: `${nextStepLabel} failed`,
          field_warning: error?.message || 'Next step failed',
          error_response_code: error?.code || error?.status || 'NA'
        })
      } finally {
        setInternalNextStepLoading(false)
      }
    }
  }

  const content = useMemo(() => {
    const commonElements = getById(pageContent, 'commonElements')[selectedLanguage]
    return {
      chevronBack: getImgByTitle(commonElements, 'chevronBack')?.url
    }
  }, [pageContent, selectedLanguage])

  const renderNextStepButton = () => (
    <div className={`w-full ${buttonMarginClass} sm:w-[135px]`}>
      <KoodoButton
        variant={{ priority: 'high', width: isMobile ? 'full' : 'responsive' }}
        tokens={{
          borderRadius: 4,
          ...(!isMobile ? { height: 44 } : {}),
          minWidth: 120,
          paddingLeft: 18,
          paddingRight: 18
        }}
        onClick={onNextStepClick}
        loading={isNextStepLoading}
        loadingLabel={nextStepLabel}
        testId={dataTestid}
      >
        {nextStepLabel}
      </KoodoButton>
    </div>
  )

  const renderTextLinkLabel = (label) => (
    <Typography
      tag="span"
      variant={{ size: 16, bold: true, color: 'var(--color-primary)' }}
      tokens={FOOTER_LINK_TOKENS}
    >
      {label}
    </Typography>
  )

  const mobileFooter = () => {
    return (
      <Box
        className={`flex ${nextStepTextButton ?
          `${hideNextStep ? 'flex-row' : 'flex-row-reverse'} justify-between` : 'flex-col items-start'
          } w-full md:justify-start ${footerPaddingClass}`}
      >
        {!hideNextStep && (
          <>
            {nextStepTextButton ? (
              <div>
                <button type="button" onClick={onNextStepClick} data-testid={dataTestid}>
                  <span className="pr-2 underline inline-block cursor-pointer">
                    {renderTextLinkLabel(nextStepLabel)}
                  </span>
                </button>
                <Image
                  alt="next button icon"
                  src={content?.chevronBack}
                  width={8}
                  className="w-2 inline-block transform rotate-180"
                />
              </div>
            ) : renderNextStepButton()}
          </>
        )}
        {hasPreviousStep && (
          <div className={`${nextStepTextButton ? 'my-0' : previousStepMarginClass}`}>
            <Image alt="back button" src={content?.chevronBack} width={8} className="w-2 inline-block" />
            <Link to={previousStepLink} onClick={handlePreviousStepClick}>
              <span className="pl-2 underline inline-block">
                {renderTextLinkLabel(previousStepLabel)}
              </span>
            </Link>
          </div>
        )}
      </Box>
    )
  }

  const regularFooter = () => {
    return (
      <Box
        className={`flex flex-row w-full ${hasPreviousStep ? 'md:justify-between' : 'md:justify-start'
          } ${footerPaddingClass} items-center`}
      >
        <div>
          {hasPreviousStep && (
            <>
              <Image width={8} alt="back button icon" src={content?.chevronBack} className="w-2 inline-block" />
              <Link to={previousStepLink} data-testid="account-setup" onClick={handlePreviousStepClick}>
                <span className="pl-2 underline inline-block">
                  {renderTextLinkLabel(previousStepLabel)}
                </span>
              </Link>
            </>
          )}
        </div>
        {!hideNextStep && (
          <>
            {nextStepTextButton ? (
              <div>
                <button type="button" onClick={onNextStepClick} data-testid={dataTestid}>
                  <span className="pr-2 underline inline-block cursor-pointer">
                    {renderTextLinkLabel(nextStepLabel)}
                  </span>
                </button>
                <Image
                  width={8}
                  alt="next button icon"
                  src={content?.chevronBack}
                  className="w-2 inline-block transform rotate-180"
                />
              </div>
            ) : renderNextStepButton()}
          </>
        )}
      </Box>
    )
  }

  return isMobile ? mobileFooter() : regularFooter()
}

SimSelectionFooter.propTypes = {
  nextStepLabel: PropTypes.string.isRequired,
  previousStepLabel: PropTypes.string,
  previousStepLink: PropTypes.string,
  nextStepLink: PropTypes.string,
  onNextStepClick: PropTypes.func,
  hideNextStep: PropTypes.bool,
  nextStepLoading: PropTypes.bool,
  compactSpacing: PropTypes.bool
}

export default SimSelectionFooter
